import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { candidatesAPI, feedbackAPI } from '../api/client';

/**
 * Candidate Assessment Feedback Page
 *
 * Flow: CandidatesList → Feedback button → navigate('/feedback/:id')
 *       → Route /feedback/:id → this component → load candidate via candidatesAPI.getById
 *
 * Persistence: drafts saved to localStorage per candidate (client-side only).
 * Scoring matrix is fully functional: add/remove rows, live score = weight×rating÷100,
 * total, percentage, recommendation, and technical level badge.
 */
let rowSeq = 0;
const nextRowId = () => `row_${Date.now()}_${rowSeq++}`;

const createEmptyRow = () => ({
  id: nextRowId(),
  evaluationArea: '',
  weight: '',
  rating: '',
  score: 0,
  remarks: '',
  whatWasAssessed: '',
  observation: '',
  improvementSuggestion: '',
});

const RATINGS = [
  { value: 100, label: '★★★★★ Outstanding' },
  { value: 80, label: '★★★★☆ Strong' },
  { value: 60, label: '★★★☆☆ Average' },
  { value: 40, label: '★★☆☆☆ Basic' },
  { value: 20, label: '★☆☆☆☆ Poor' },
];

const calculateRowScore = (weight, rating) => {
  const w = Number(weight) || 0;
  const r = Number(rating) || 0;
  return (w * r) / 100;
};

const getDraftKey = (candidateId) => `candidate_feedback_${candidateId}`;

const EMPTY_FORM = {
  candidateName: '',
  position: '',
  assessmentDate: '',
  assessedBy: '',
  summary: '',
  assessmentResult: 'Strongly Aligned',
  alignment: 'Strong',
  topStrength1: '',
  topStrength2: '',
  topStrength3: '',
  areasToValidate: '',
  overallScore: '',
  finalRecommendation: 'Recommend',
  approvedBy: '',
  approvalDate: '',
  hmComments: '',
};

const createCandidateForm = (candidate) => ({
  ...EMPTY_FORM,
  candidateName: `${candidate.first_name || ''} ${candidate.last_name || ''}`.trim(),
  position: candidate.position || '',
});

const createSubmittedForm = (candidateForm, feedback) => ({
  ...candidateForm,
  assessmentDate: feedback.assessment_date || '',
  assessedBy: feedback.assessed_by || '',
  summary: feedback.assessment_summary || '',
  assessmentResult: feedback.assessment_result || candidateForm.assessmentResult,
  alignment: feedback.candidate_alignment || candidateForm.alignment,
  topStrength1: feedback.top_strength_1 || '',
  topStrength2: feedback.top_strength_2 || '',
  topStrength3: feedback.top_strength_3 || '',
  areasToValidate: feedback.areas_to_validate || '',
  overallScore: feedback.overall_score || '',
  finalRecommendation: feedback.final_recommendation || candidateForm.finalRecommendation,
  approvedBy: feedback.approved_by || '',
  approvalDate: feedback.approval_date || '',
  hmComments: feedback.hiring_manager_comments || '',
});

const createSubmittedScoreRow = (row) => ({
  id: `score_${row.id}`,
  evaluationArea: row.evaluation_area || '',
  weight: row.weight ?? '',
  rating: row.rating ?? '',
  score: row.score ?? 0,
  remarks: row.remarks || '',
  whatWasAssessed: row.what_was_assessed || '',
  observation: row.candidate_observation || '',
  improvementSuggestion: row.improvement_suggestion || '',
});

const getDraft = (candidateId) => {
  try {
    const raw = localStorage.getItem(getDraftKey(candidateId));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const getExportFilename = (candidate) => {
  const candidateName = `${candidate.first_name || ''} ${candidate.last_name || ''}`
    .trim()
    .replace(/[^a-zA-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '') || 'Candidate';
  return `Candidate_Feedback_${candidateName}_${candidate.id}.xlsx`;
};

export default function CandidateFeedback() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [candidate, setCandidate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const [form, setForm] = useState(EMPTY_FORM);
  const [scoreRows, setScoreRows] = useState(() => [createEmptyRow()]);
  const [draftStatus, setDraftStatus] = useState('');
  const [requests, setRequests] = useState([]);
  const [resending, setResending] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const fetchCandidateAndFeedback = async () => {
      try {
        setLoading(true);
        setError(false);
        const [candidateResponse, feedbackResponse, requestsResponse] = await Promise.all([
          candidatesAPI.getById(id),
          feedbackAPI.getByCandidate(id),
          feedbackAPI.getRequests(id).catch(() => ({ data: [] })),
        ]);
        if (!cancelled) {
          const candidateData = candidateResponse.data;
          const submittedFeedback = feedbackResponse.data?.feedback;
          const candidateForm = createCandidateForm(candidateData);

          setCandidate(candidateData);
          setRequests(requestsResponse.data || []);
          if (submittedFeedback) {
            setForm(createSubmittedForm(candidateForm, submittedFeedback));
            setScoreRows(feedbackResponse.data.scoreRows.map(createSubmittedScoreRow));
            setDraftStatus('Submitted feedback loaded.');
          } else {
            const draft = getDraft(candidateData.id);
            setForm(draft?.form ? { ...candidateForm, ...draft.form } : candidateForm);
            setScoreRows(
              Array.isArray(draft?.scoreRows) && draft.scoreRows.length
                ? draft.scoreRows
                : [createEmptyRow()]
            );
            setDraftStatus(
              draft?.savedAt
                ? `Draft loaded (saved ${new Date(draft.savedAt).toLocaleString('en-IN')})`
                : ''
            );
          }
        }
      } catch {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchCandidateAndFeedback();
    return () => { cancelled = true; };
  }, [id]);

  const totals = scoreRows.reduce(
    (acc, row) => {
      acc.totalScore += Number(row.score) || 0;
      acc.totalWeight += Number(row.weight) || 0;
      return acc;
    },
    { totalScore: 0, totalWeight: 0 }
  );

  const percentage = totals.totalWeight > 0 ? (totals.totalScore / totals.totalWeight) * 100 : 0;

  const autoRecommendation =
    percentage >= 85
      ? 'Strongly Recommended'
      : percentage >= 70
        ? 'Recommended'
        : percentage >= 55
          ? 'Consider'
          : 'Not Recommended';

  const technicalLevel =
    totals.totalScore >= 85
      ? { label: 'Expert', color: '#198754' }
      : totals.totalScore >= 70
        ? { label: 'Advanced', color: '#0d6efd' }
        : totals.totalScore >= 55
          ? { label: 'Intermediate', color: '#fd7e14' }
          : { label: 'Beginner', color: '#dc3545' };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleScoreChange = (rowId, field, value) => {
    setScoreRows((rows) =>
      rows.map((row) => {
        if (row.id !== rowId) return row;
        const updated = { ...row, [field]: value };
        if (field === 'weight' || field === 'rating') {
          updated.score = calculateRowScore(updated.weight, updated.rating);
        }
        return updated;
      })
    );
  };

  const addScoreRow = () => {
    setScoreRows((rows) => [...rows, createEmptyRow()]);
  };

  const removeScoreRow = (rowId) => {
    setScoreRows((rows) => {
      if (rows.length <= 1) {
        toast.error('Minimum one Evaluation Area is required.');
        return rows;
      }
      return rows.filter((r) => r.id !== rowId);
    });
  };

  const saveDraft = () => {
    if (!candidate) return;
    const key = getDraftKey(candidate.id);
    localStorage.setItem(
      key,
      JSON.stringify({ form, scoreRows, savedAt: new Date().toISOString() })
    );
    setDraftStatus(`Draft saved at ${new Date().toLocaleTimeString('en-IN')}`);
    toast.success('Draft saved locally.');
  };

  const handleSubmitFeedback = async () => {
    if (!form.candidateName.trim()) {
      toast.error('Candidate Name is required.');
      return;
    }
    if (!form.position.trim()) {
      toast.error('Position is required.');
      return;
    }
    if (!scoreRows.some((r) => r.evaluationArea.trim())) {
      toast.error('Add at least one Evaluation Area in the Scoring Matrix.');
      return;
    }

    saveDraft();
    try {
      await feedbackAPI.create({
        candidateId: candidate.id,
        form,
        scoreRows,
        totalScore: totals.totalScore,
        percentage,
        autoRecommendation,
        technicalLevel: technicalLevel.label,
      });
      setDraftStatus('Feedback submitted successfully');
      toast.success('Feedback submitted successfully.');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to submit feedback. Please try again.');
    }
  };

  const handleExportFeedback = async () => {
    try {
      const response = await feedbackAPI.export(candidate.id);
      const contentDisposition = response.headers['content-disposition'] || '';
      const filename = contentDisposition.match(/filename="?([^";]+)"?/)?.[1]
        || getExportFilename(candidate);
      const url = URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      let message = 'Failed to export feedback. Please try again.';
      if (err.response?.data instanceof Blob) {
        try {
          const body = JSON.parse(await err.response.data.text());
          message = body.error || message;
        } catch {
          // Keep the default message when the error response is not JSON.
        }
      } else {
        message = err.response?.data?.error || message;
      }
      toast.error(message);
    }
  };

  const handleResendRequest = async () => {
    if (!candidate || resending) return;
    setResending(true);
    try {
      await feedbackAPI.resend(candidate.id);
      const updated = await feedbackAPI.getRequests(candidate.id).catch(() => ({ data: [] }));
      setRequests(updated.data || []);
      toast.success('Feedback request email sent.');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to resend the feedback request.');
    } finally {
      setResending(false);
    }
  };

  if (loading) {
    return (
      <>
        <style>{css}</style>
        <div className="candidate-feedback-page">
          <div className="candidate-feedback-card">
            <div className="loading-spinner"></div>
            <h3>Loading candidate details...</h3>
          </div>
        </div>
      </>
    );
  }

  if (error || !candidate) {
    return (
      <>
        <style>{css}</style>
        <div className="candidate-feedback-page">
          <div className="candidate-feedback-card">
            <p>Candidate not found</p>
            <button className="btn-back" onClick={() => navigate('/candidates')}>
              Back to List
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <style>{css}</style>
      <div className="candidate-feedback-page">
        <div className="candidate-feedback-card">

          <div className="card-header">
            <div className="header-left">
              <div className="avatar-large">
                {(candidate.first_name || 'C')[0].toUpperCase()}
              </div>
              <div className="header-info">
                <h1 className="candidate-name">{candidate.first_name} {candidate.last_name}</h1>
                <p className="candidate-id">ID: #{candidate.id}</p>
              </div>
            </div>
            <div className="header-actions">
              <button className="btn-back" onClick={handleExportFeedback}>Export Excel</button>
              <button className="btn-back" onClick={() => navigate('/candidates')}>Back</button>
            </div>
          </div>

          {requests.length > 0 && (
            <div className="request-panel">
              <div className="request-panel-head">
                <h3>Feedback Requests</h3>
                {requests.some((r) => r.status !== 'submitted') && (
                  <button
                    className="btn-resend"
                    onClick={handleResendRequest}
                    disabled={resending}
                  >
                    {resending ? 'Sending…' : 'Resend Email'}
                  </button>
                )}
              </div>
              {requests.map((r) => (
                <div className={`request-row status-${r.status}`} key={r.id}>
                  <span className="request-recipient">{r.reviewer_name || r.reviewer_email}</span>
                  <span className={`status-badge ${r.status}`}>{r.status}</span>
                  <span className="request-meta">
                    {r.status === 'submitted'
                      ? `Submitted ${r.submitted_at ? new Date(r.submitted_at).toLocaleString('en-IN') : ''}`
                      : r.status === 'expired'
                        ? `Expired ${r.expires_at ? new Date(r.expires_at).toLocaleString('en-IN') : ''}`
                        : `Link valid until ${r.expires_at ? new Date(r.expires_at).toLocaleString('en-IN') : ''}`}
                  </span>
                </div>
              ))}
            </div>
          )}

          <div className="feedback-body">
            <h2>Candidate Assessment</h2>

            <div className="section">
              <h3 className="section-title">Candidate Details</h3>
              <div className="grid-2">
                <div>
                  <label>Candidate Name</label>
                  <input
                    name="candidateName"
                    value={form.candidateName}
                    onChange={handleChange}
                    placeholder="Enter Candidate Name"
                  />
                </div>
                <div>
                  <label>Position</label>
                  <input
                    name="position"
                    value={form.position}
                    onChange={handleChange}
                    placeholder="Enter Position"
                  />
                </div>
                <div>
                  <label>Assessment Date</label>
                  <input
                    type="date"
                    name="assessmentDate"
                    value={form.assessmentDate}
                    onChange={handleChange}
                  />
                </div>
                <div>
                  <label>Assessed By</label>
                  <input
                    name="assessedBy"
                    value={form.assessedBy}
                    onChange={handleChange}
                    placeholder="Interviewer Name"
                  />
                </div>
              </div>
            </div>

            <div className="section">
              <h3 className="section-title">Assessment Summary</h3>
              <label>Assessment Summary</label>
              <textarea
                rows={4}
                name="summary"
                value={form.summary}
                onChange={handleChange}
                placeholder="Write overall assessment..."
              />
              <br /><br />
              <label>Assessment Result</label>
              <select name="assessmentResult" value={form.assessmentResult} onChange={handleChange}>
                <option>Strongly Aligned</option>
                <option>Aligned</option>
                <option>Partially Aligned</option>
                <option>Not Recommended</option>
              </select>
            </div>

            <div className="section">
              <h3 className="section-title">Scoring Matrix</h3>
              <table className="matrix-table">
                <thead>
                  <tr>
                    <th scope="col" style={{ width: '30%' }}>Evaluation Area</th>
                    <th scope="col" style={{ width: '10%' }}>Weight</th>
                    <th scope="col" style={{ width: '18%' }}>Rating</th>
                    <th scope="col" style={{ width: '10%' }}>Score</th>
                    <th scope="col">Remarks</th>
                    <th scope="col" style={{ width: '10%' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {scoreRows.map((row) => (
                    <tr key={row.id}>
                      <td>
                        <input
                          value={row.evaluationArea}
                          onChange={(e) => handleScoreChange(row.id, 'evaluationArea', e.target.value)}
                          placeholder="Enter Evaluation Area"
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          value={row.weight}
                          onChange={(e) => handleScoreChange(row.id, 'weight', e.target.value)}
                          placeholder="%"
                          min="0"
                          max="100"
                        />
                      </td>
                      <td>
                        <select
                          value={row.rating}
                          onChange={(e) => handleScoreChange(row.id, 'rating', e.target.value)}
                        >
                          <option value="">Select</option>
                          {RATINGS.map((r) => (
                            <option key={r.value} value={r.value}>{r.label}</option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <input className="score" value={(Number(row.score) || 0).toFixed(1)} readOnly />
                      </td>
                      <td>
                        <input
                          value={row.remarks}
                          onChange={(e) => handleScoreChange(row.id, 'remarks', e.target.value)}
                          placeholder="Interview Remarks"
                        />
                      </td>
                      <td>
                        <button type="button" className="removeBtn" onClick={() => removeScoreRow(row.id)}>
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <button type="button" className="addBtn" onClick={addScoreRow}>
                + Add Evaluation Area
              </button>
              <div className="summaryRow">
                <div>Total: <span className="summary-value">{totals.totalScore.toFixed(1)}</span></div>
                <div>Percentage: <span className="summary-value">{percentage.toFixed(1)}%</span></div>
                <div>Recommendation: <span className="summary-value">{autoRecommendation}</span></div>
              </div>
            </div>

            <div className="section">
              <h3 className="section-title">Evaluation Guidelines</h3>
              {scoreRows.filter((r) => r.evaluationArea.trim()).length === 0 ? (
                <p className="guide-placeholder">
                  Enter an Evaluation Area in the Scoring Matrix above to generate guidelines.
                </p>
              ) : (
                scoreRows
                  .filter((r) => r.evaluationArea.trim())
                  .map((row) => (
                    <div className="guide-card" key={row.id}>
                      <h4>{row.evaluationArea}</h4>
                      <div className="grid-2">
                        <div>
                          <label>What Was Assessed</label>
                          <input
                            value={row.whatWasAssessed}
                            onChange={(e) => handleScoreChange(row.id, 'whatWasAssessed', e.target.value)}
                            placeholder="Assessment Criteria"
                          />
                        </div>
                        <div>
                          <label>Candidate Observation</label>
                          <input
                            value={row.observation}
                            onChange={(e) => handleScoreChange(row.id, 'observation', e.target.value)}
                            placeholder="Observation"
                          />
                        </div>
                        <div>
                          <label>Improvement Suggestion</label>
                          <input
                            value={row.improvementSuggestion}
                            onChange={(e) => handleScoreChange(row.id, 'improvementSuggestion', e.target.value)}
                            placeholder="Improvement Suggestion"
                          />
                        </div>
                        <div>
                          <label>Area Name</label>
                          <input value={row.evaluationArea} readOnly />
                        </div>
                      </div>
                    </div>
                  ))
              )}
            </div>

            <div className="section">
              <h3 className="section-title">Final Assessment</h3>
              <div className="grid-2">
                <div>
                  <label>Candidate Alignment</label>
                  <select name="alignment" value={form.alignment} onChange={handleChange}>
                    <option>Strong</option>
                    <option>Moderate</option>
                    <option>Weak</option>
                  </select>
                </div>
                <div>
                  <label>Overall Technical Level</label>
                  <div className="badge" style={{ background: technicalLevel.color }}>{technicalLevel.label}</div>
                </div>
                <div>
                  <label>Top Strength 1</label>
                  <input name="topStrength1" value={form.topStrength1} onChange={handleChange} />
                </div>
                <div>
                  <label>Top Strength 2</label>
                  <input name="topStrength2" value={form.topStrength2} onChange={handleChange} />
                </div>
                <div>
                  <label>Top Strength 3</label>
                  <input name="topStrength3" value={form.topStrength3} onChange={handleChange} />
                </div>
              </div>
              <br />
              <label>Areas To Validate</label>
              <textarea
                rows={3}
                name="areasToValidate"
                value={form.areasToValidate}
                onChange={handleChange}
                placeholder="Mention the areas which require additional validation before hiring..."
              />
              <hr className="feedback-divider" />
              <div className="grid-2">
                <div>
                  <label>Overall Score (Hiring Manager)</label>
                  <input
                    name="overallScore"
                    value={form.overallScore}
                    onChange={handleChange}
                    placeholder="Enter Overall Score"
                  />
                </div>
                <div>
                  <label>Final Recommendation</label>
                  <select name="finalRecommendation" value={form.finalRecommendation} onChange={handleChange}>
                    <option>Recommend</option>
                    <option>Hold</option>
                    <option>Reject</option>
                  </select>
                </div>
                <div>
                  <label>Approved By</label>
                  <input name="approvedBy" value={form.approvedBy} onChange={handleChange} />
                </div>
                <div>
                  <label>Approval Date</label>
                  <input type="date" name="approvalDate" value={form.approvalDate} onChange={handleChange} />
                </div>
              </div>
              <br />
              <label>Hiring Manager Comments</label>
              <textarea rows={3} name="hmComments" value={form.hmComments} onChange={handleChange} />

              {draftStatus && <p className="draft-status">{draftStatus}</p>}

              <div className="buttons">
                <button type="button" className="save" onClick={saveDraft}>Save Draft</button>
                <button type="button" className="submit" onClick={handleSubmitFeedback}>Submit Feedback</button>
              </div>
            </div>

          </div>
        </div>
      </div>
    </>
  );
}

const css = `
.candidate-feedback-page {
  min-height: auto;
  background: transparent;
  padding: 4px 0;
  font-family: var(--font-sans);
  animation: fadeInUp 0.5s ease-out;
}

.candidate-feedback-card {
  background: #FFFFFF;
  max-width: 1200px;
  margin: 0 auto;
  border-radius: 16px;
  box-shadow: var(--shadow-lg);
  overflow: hidden;
  animation: slideUp 0.6s ease-out;
}

.card-header {
  background: linear-gradient(135deg, #1E40AF, #1e3a8a);
  padding: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: white;
  gap: 14px;
  flex-wrap: wrap;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 14px;
}

.avatar-large {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: linear-gradient(135deg, #3B82F6, #0EA5E9);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  font-weight: bold;
  color: white;
  box-shadow: var(--shadow-md);
  flex-shrink: 0;
}

.header-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.candidate-name {
  margin: 0;
  font-size: 24px;
  font-weight: 700;
  color: white;
}

.candidate-id {
  margin: 4px 0 0;
  font-size: 13px;
  opacity: 0.85;
  color: white;
}

.header-actions {
  display: flex;
  gap: 10px;
  align-items: center;
}

.btn-back {
  padding: 8px 18px;
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.12);
  color: white;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: var(--font-sans);
}

.btn-back:hover {
  background: rgba(255, 255, 255, 0.25);
}

.feedback-body {
  padding: 24px;
}

.request-panel {
  margin: 16px 24px 0;
  background: #f0f7ff;
  border: 1px solid #bcd9ff;
  border-radius: 12px;
  padding: 16px;
}

.request-panel-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 10px;
}

.request-panel-head h3 {
  margin: 0;
  font-size: 15px;
  color: #1746A2;
}

.request-row {
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
  padding: 8px 0;
  border-top: 1px solid #dcebff;
}

.request-recipient {
  font-weight: 600;
  font-size: 14px;
  color: #0F172A;
  min-width: 200px;
}

.request-meta {
  font-size: 13px;
  color: #64748B;
  flex: 1;
}

.status-badge {
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
}

.status-badge.pending {
  background: #fff7e0;
  color: #b45309;
}

.status-badge.expired {
  background: #fee2e2;
  color: #b91c1c;
}

.status-badge.submitted {
  background: #d1fae5;
  color: #047857;
}

.btn-resend {
  padding: 8px 18px;
  border: none;
  border-radius: 8px;
  background: #1746A2;
  color: white;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  font-family: var(--font-sans);
}

.btn-resend:hover {
  background: #0f3d8a;
}

.btn-resend:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.feedback-body h2 {
  color: #1746A2;
  margin-bottom: 20px;
  font-size: 22px;
}

.section {
  background: #fff;
  padding: 24px;
  border-radius: 14px;
  margin-bottom: 24px;
  box-shadow: 0 8px 20px rgba(0,0,0,.06);
  border: 1px solid #E2E8F0;
}

.section-title {
  color: #1746A2;
  margin-bottom: 16px;
  font-size: 16px;
  font-weight: 700;
}

.grid-2 {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
}

.feedback-body label {
  display: block;
  margin-bottom: 6px;
  font-size: 14px;
  font-weight: 600;
  color: #0F172A;
}

.feedback-body input,
.feedback-body select,
.feedback-body textarea {
  width: 100%;
  padding: 12px;
  border-radius: 8px;
  border: 1px solid #d9d9d9;
  font-size: 14px;
  outline: none;
  font-family: var(--font-sans);
  box-sizing: border-box;
}

.feedback-body input:focus,
.feedback-body select:focus,
.feedback-body textarea:focus {
  border-color: #1746A2;
}

.feedback-body textarea {
  min-height: 100px;
  resize: vertical;
}

.matrix-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 20px;
}

.matrix-table th {
  background: #1746A2;
  color: white;
  padding: 12px;
  font-size: 13px;
}

.matrix-table td {
  padding: 10px;
  border-bottom: 1px solid #ececec;
}

.matrix-table input,
.matrix-table select {
  width: 100%;
  padding: 10px;
  border-radius: 6px;
  border: 1px solid #ccc;
  box-sizing: border-box;
}

.score {
  background: #f4f6fa;
  font-weight: bold;
  text-align: center;
}

.removeBtn {
  background: #dc3545;
  color: white;
  padding: 8px 15px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 13px;
  width: 100%;
}

.removeBtn:hover {
  background: #b71c1c;
}

.addBtn {
  background: #28a745;
  color: white;
  padding: 12px 25px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  margin-top: 15px;
}

.addBtn:hover {
  background: #218838;
}

.summaryRow {
  display: flex;
  justify-content: flex-end;
  gap: 40px;
  margin-top: 25px;
  font-weight: 600;
  font-size: 16px;
  flex-wrap: wrap;
}

.summary-value {
  color: #1746A2;
}

.guide-card {
  background: #f8fbff;
  border: 1px solid #dfeeff;
  border-radius: 10px;
  padding: 20px;
  margin-top: 20px;
}

.guide-card h4 {
  color: #1746A2;
  margin-bottom: 15px;
}

.guide-placeholder {
  color: #64748B;
  font-size: 14px;
  margin: 0;
}

.badge {
  display: inline-block;
  padding: 8px 18px;
  border-radius: 20px;
  background: #1746A2;
  color: white;
  font-size: 14px;
  font-weight: 600;
}

.buttons {
  margin-top: 30px;
  text-align: center;
  display: flex;
  gap: 16px;
  justify-content: center;
}

.save {
  background: #d9e4ff;
  color: #1746A2;
  padding: 12px 25px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 15px;
  font-weight: 600;
}

.save:hover {
  background: #c5d4f5;
}

.submit {
  background: #1746A2;
  color: white;
  padding: 12px 25px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 15px;
  font-weight: 600;
}

.submit:hover {
  background: #0f3d8a;
}

.feedback-divider {
  margin: 24px 0;
  border: none;
  border-top: 1px solid #E2E8F0;
}

.draft-status {
  margin: 20px 0 0;
  text-align: center;
  font-size: 13px;
  font-weight: 600;
  color: #10B981;
}

.loading-spinner {
  font-size: 36px;
  margin-bottom: 12px;
  animation: spin 2s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

@media (max-width: 900px) {
  .grid-2 {
    grid-template-columns: 1fr;
  }
}
`;
